import { useCallback, useEffect, useRef, useState } from 'react';
import CalcAPI from '~/app/api/calcAPI';
import SocketWorker from '~/socket.worker.js';

/**
 * Manages the WebSocket / CalcAPI lifecycle for a tax return session.
 *
 * Responsibilities:
 *   - Spin up a SocketWorker + CalcAPI on mount
 *   - Handle low-level connection events: CLOSE, REFRESH, CRITICAL, SOCKET_OPEN
 *   - Forward all business-level JSON messages to the caller via onMessage
 *   - Tear down cleanly on unmount
 *
 * NOT responsible for:
 *   - Business logic inside socket responses (form_load, calc_result, etc.)
 *   - Auto-save, ReturnInit, or any return-specific workflow
 *
 * @param {object}   params
 * @param {number}   params.returnID            - The open return ID
 * @param {Array<unknown>} params.ddh           - Drill-down history array for the current session.
 *                                                Consumed by CalcAPI / UtilityAPI.getLastDrilledOfficeID.
 * @param {React.MutableRefObject<boolean>} params.returnInitializedRef
 *                                              - Ref tracking whether ReturnInit has been sent.
 *                                                The caller owns this ref; the hook reads it when
 *                                                (re-)creating CalcAPI after a reconnect.
 * @param {function} params.onOpen              - Called with (calcAPI) when SOCKET_OPEN is received.
 *                                                Use this to send ReturnInit, start auto-save, etc.
 * @param {function} params.onMessage           - Called with (parsedCommand, calcAPI) for every
 *                                                JSON message from the calc server.
 * @param {function} params.onClose             - Called when the server sends a CLOSE signal.
 *
 * @returns {{
 *   calcAPIRef: React.MutableRefObject<CalcAPI|null>,
 *   socketWorkerRef: React.MutableRefObject<Worker|null>,
 *   connectionStatus: boolean
 * }}
 */
const useCalcSession = ({ returnID, ddh, returnInitializedRef, onOpen, onMessage, onClose }) => {
  const calcAPIRef = useRef(null);
  const socketWorkerRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState(true);

  // Keep the latest callbacks in refs so the message handler never closes over stale versions.
  const onOpenRef = useRef(onOpen);
  const onMessageRef = useRef(onMessage);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const init = useCallback(() => {
    // Terminate the previous worker before replacing it to prevent orphaned threads.
    const prevWorker = socketWorkerRef.current;
    if (prevWorker) {
      prevWorker.onmessage = null;
      prevWorker.terminate();
    }

    const worker = new SocketWorker();
    const api = new CalcAPI(
      worker,
      parseInt(returnID, 10),
      ddh,
      returnInitializedRef?.current ?? false,
    );

    worker.onmessage = async evt => {
      if (!evt || !evt.data) return;

      // String signals from the socket worker (not JSON calc-server responses)
      switch (evt.data) {
        case 'CLOSE':
          setConnectionStatus(false);
          onCloseRef.current?.();
          return;

        case 'REFRESH':
          // Reconnect requested (e.g. token expired) — replace the current session
          api.CloseSocket();
          init();
          return;

        case 'CRITICAL':
          // Unrecoverable worker state — attempt a full reconnect
          setConnectionStatus(false);
          api.CloseSocket();
          console.warn(
            '[useCalcSession] CRITICAL socket state. Attempting to reopen connection...',
          );
          init();
          return;

        case 'SOCKET_OPEN':
          setConnectionStatus(true);
          onOpenRef.current?.(api);
          return;

        default:
          break;
      }

      // All other messages are JSON from the calc server
      try {
        const parsed = JSON.parse(evt.data);
        onMessageRef.current?.(parsed, api);
      } catch (err) {
        console.error('[useCalcSession] Failed to parse socket message:', err, evt.data);
      }
    };

    socketWorkerRef.current = worker;
    calcAPIRef.current = api;
  }, [returnID, ddh, returnInitializedRef]);

  useEffect(() => {
    init();
    return () => {
      const api = calcAPIRef.current;
      const worker = socketWorkerRef.current;
      api?.CloseSession();
      if (worker) {
        worker.onmessage = null;
        worker.terminate();
      }
      socketWorkerRef.current = null;
      calcAPIRef.current = null;
    };
  }, [init]);

  return { calcAPIRef, socketWorkerRef, connectionStatus };
};

export default useCalcSession;
